import { supabase } from './supabase';

// Issue #299, Session 74z5. Vendor credit memos.
//
// A vendor bills for a hundred, ships eighty, and credits the difference back.
// Before this there was nowhere to put that document: cancelling is wrong because
// the goods arrived, editing the posted invoice destroys the audit trail the
// three-way match exists to produce, and a second positive invoice increases what
// D&R owes. So the order stayed permanently overstated on the surface that feeds
// QuickBooks.
//
// AMOUNTS ARE STORED POSITIVE ON BOTH DOCUMENT TYPES and the sign comes from
// document_type. QuickBooks carries a Vendor Credit as its own object rather than a
// negative Bill, so this keeps the export mapping one-to-one. The sign is applied in
// exactly one place in the database — material_request_item_invoiced_total() — and in
// exactly one place here, creditedTotal() below. Never store a negative.
//
// A separate module rather than more surface on invoiceDiscrepancy.js: a discrepancy
// is a judgment about an invoice, a credit is a different document.

export const DOC_INVOICE = 'invoice';
export const DOC_CREDIT = 'credit';

export function isCredit(doc) {
  return doc?.document_type === DOC_CREDIT;
}

export function isInvoiceDoc(doc) {
  // Rows written before this migration have document_type defaulted to 'invoice',
  // so a null check would be wrong here — the column is NOT NULL.
  return doc?.document_type !== DOC_CREDIT;
}

function num(v) {
  if (v == null) return 0;
  const n = parseFloat(v);
  return isNaN(n) ? 0 : n;
}

// Every credit written against one invoice.
export function creditsOf(docs, invoiceId) {
  if (!docs || invoiceId == null) return [];
  return docs.filter(d => isCredit(d) && String(d.credit_of_invoice_id) === String(invoiceId));
}

// What the vendor billed, before any credit.
export function billedTotal(docs) {
  return (docs || []).filter(isInvoiceDoc).reduce((sum, d) => sum + num(d.total_amount), 0);
}

// What the vendor credited back. Returned POSITIVE — callers render the minus sign.
export function creditedTotal(docs) {
  return (docs || []).filter(isCredit).reduce((sum, d) => sum + num(d.total_amount), 0);
}

// What D&R actually owes. This is the number that was previously impossible to
// produce, and the reason #299 is HIGH rather than MEDIUM.
export function netInvoiced(docs) {
  return billedTotal(docs) - creditedTotal(docs);
}

// How much of one invoice can still be credited. The database enforces this too
// (enter_material_request_credit raises if exceeded); this exists so the sheet can
// show the ceiling before the user hits it rather than after.
export function remainingCreditable(invoice, docs) {
  const already = creditsOf(docs, invoice?.id).reduce((sum, c) => sum + num(c.total_amount), 0);
  return Math.max(0, num(invoice?.total_amount) - already);
}

// Per-line ceiling: what this invoice billed for one order line, less what has
// already been credited against that line on this invoice.
export function remainingCreditableForLine(invoice, docs, lines, requestItemId) {
  const billed = (lines || [])
    .filter(l => String(l.invoice_id) === String(invoice?.id)
      && String(l.request_item_id) === String(requestItemId))
    .reduce((sum, l) => sum + num(l.line_amount), 0);

  const creditIds = creditsOf(docs, invoice?.id).map(c => String(c.id));
  const credited = (lines || [])
    .filter(l => creditIds.includes(String(l.invoice_id))
      && String(l.request_item_id) === String(requestItemId))
    .reduce((sum, l) => sum + num(l.line_amount), 0);

  return { billed, credited, remaining: Math.max(0, billed - credited) };
}

// Locked rule 7: the credit header, its lines, the per-item rollup and the request
// header all move together, so this is one rpc() call and never chained inserts.
//
// items is [{ request_item_id, credit_amount }] with credit_amount POSITIVE.
// Lines with a zero or absent amount are skipped by the function, so the caller can
// pass every line of the invoice and let the user fill in only the ones being
// credited.
export async function enterVendorCredit({
  requestId, userId, creditOfInvoiceId, items, creditRef, creditDate,
}) {
  const { data, error } = await supabase.rpc('enter_material_request_credit', {
    p_request_id: requestId,
    p_user_id: userId,
    p_credit_of_invoice_id: creditOfInvoiceId,
    p_items: items,
    p_credit_ref: creditRef,
    p_credit_date: creditDate,
  });
  if (error) throw error;
  return data;
}
